// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 28-it-dashboard-ui.md
// App-detail surface system — class-string constants consumed throughout the
// /apps/:id re-skin (A-foundation + B–F tabs). Compose with cn() as needed.
// Surface radii: AD_CARD/AD_GROUP/AD_STAT=16px are centralized in the
// constants below; dialog (18px) and icon (22px) radii remain at their call
// sites. General Tailwind tokens (gl=6px, gl-lg=16px, gl-icon=10px) are in docs/19.

import { cn } from '@/lib/utils';

/** Core elevated surface: bg-card, hairline border, 16px radius, layered shadow. */
export const AD_CARD = cn('bg-card border border-border/70 rounded-[16px] ad-shadow');

/** iOS-style inset grouped list — same shell as AD_CARD with overflow-hidden
 *  to clip the .ad-row hairline separators. */
export const AD_GROUP = cn(
  'bg-card border border-border/70 rounded-[16px] overflow-hidden ad-shadow',
);

/** A row inside AD_GROUP: flex row with inset hairline separator above. */
export const AD_ROW = cn(
  'flex items-center gap-3.5 px-[18px] py-3.5 min-h-[56px]',
  'border-t border-border/50 first:border-t-0',
);

/** Interactive row variant — adds hover fill. */
export const AD_ROW_INT = cn(AD_ROW, 'transition-colors hover:bg-accent/50 cursor-pointer');

/** Card / group header bar: title + optional action, with bottom hairline. */
export const AD_CARD_HD = cn(
  'flex items-center justify-between px-5 py-4 border-b border-border/60',
);

/** Metric tile: a card with a spaced gap (not hairline). */
export const AD_STAT = cn('bg-card border border-border/70 rounded-[16px] p-4 ad-shadow');

/** Quiet section label — Geist Mono, 10.5px, uppercase, wide tracking,
 *  muted. Use sparingly; replaces the louder full-mono headers. */
export const AD_EYEBROW = cn(
  'font-mono text-[10.5px] uppercase tracking-[0.09em] text-[hsl(var(--text-tertiary))]',
);

/** Soft destructive-tint button — red text on a low-opacity red wash + ring,
 *  darkening on hover. Used for the app-detail power/lifecycle trigger; a
 *  deliberately quieter affordance than a solid destructive fill. */
export const AD_BTN_DESTRUCTIVE_TINT = cn(
  'rounded-[10px] text-[hsl(var(--status-red-text))]',
  'ring-1 ring-[hsl(var(--status-red)/0.4)] bg-[hsl(var(--status-red)/0.08)]',
  'hover:bg-[hsl(var(--status-red)/0.16)] hover:text-[hsl(var(--status-red-text))]',
  'focus-visible:ring-2 focus-visible:ring-[hsl(var(--status-red)/0.6)]',
);

/** Destructive menu-item tint — red text with a red focus wash, for the
 *  Delete entry in the lifecycle dropdown. */
export const AD_MENUITEM_DESTRUCTIVE = cn(
  'text-[hsl(var(--status-red-text))]',
  'focus:bg-[hsl(var(--status-red)/0.1)] focus:text-[hsl(var(--status-red-text))]',
);

/** Header metadata strip container — borderless inline flex row, wraps. */
export const AD_META = cn('flex flex-wrap items-center gap-x-0 gap-y-1');

/** One cell in the metadata strip: icon + value, with a trailing vertical divider
 *  except on the last child. */
export const AD_META_ITEM = cn(
  'inline-flex items-center gap-1.5 px-3 py-1 text-[13px] text-muted-foreground',
  'transition-colors hover:text-foreground',
  // Vertical hairline divider before each cell except the first; rendered as a
  // pseudo-element so a cell that wraps to a new line drops its own leading
  // divider (border-l on the wrapped cell would otherwise float at row start).
  'relative first:pl-0',
  "[&:not(:first-child)]:before:content-[''] [&:not(:first-child)]:before:absolute",
  '[&:not(:first-child)]:before:left-0 [&:not(:first-child)]:before:top-1/2 [&:not(:first-child)]:before:-translate-y-1/2',
  '[&:not(:first-child)]:before:h-4 [&:not(:first-child)]:before:w-px [&:not(:first-child)]:before:bg-border/60',
);
